// Copyright (c) Docugami, Inc. All rights reserved.

import { skipToken } from "@reduxjs/toolkit/dist/query"
import { ChunkDragLayer } from "components/cards/ChunkDragLayer"
import { ReportsWorkPane } from "components/cards/ReportsWorkPane"
import { NavDirection } from "components/common/DocumentNavButton"
import { WebDocHost } from "components/doc_hosting/doc_hosts/web/presentation/WebDocHost"
import { createFolderId } from "components/file_management/file_helpers"
import { DocumentViewContext } from "components/reports/ReportDocumentContext"
import { appBackgroundGrey, emptyGray } from "components/shell/presentation/AppStyles"
import { useChunkLinking } from "hooks/ChunkLinking"
import { useAppSelector } from "hooks/redux"
import { useMemo, useState } from "react"
import { useGetQualityControlWarningsQuery } from "store/frontend/frontendSlice"
import { selectClusterId, selectIsReportPublished, selectReportId, selectSelectedLabel } from "store/reports/selectors"
import styled from "styled-components"
import { DocumentStateNotification } from "./DocumentStateNotification"
import { FilmStrip } from "./DocumentViewCanvas"
import { ReportDocumentNavButton } from "./ReportDocumentNavButton"
import { GenericContainer } from "./WorkPathStyles"
import { useLabelChunkMultiSelect } from "hooks/LabelChunksMultiSelect"

const DocumentView = styled.div`
    display: flex;
    flex-direction: row;
    height: 100%;
`

const CanvasContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background: ${appBackgroundGrey};
`

const SingleDocumentViewContainer = styled.div`
    display: flex;
    flex: 1;
    height: 100%;
`

const DocumentContainer = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
`

const TaskPanesContainer = styled(GenericContainer)`
    flex-direction: column;
    width: 350px;
    border-radius: 0;
    padding: 5px;
    border-left: 1px solid ${emptyGray};
`;

interface ReportDocumentViewerProps {
    targetDocumentId: string;
    documentIds?: string[];
}

export const ReportDocumentViewer = (props: ReportDocumentViewerProps): JSX.Element => {
    const [propertiesHidden, setPropertiesHidden] = useState(true);

    const reportId = useAppSelector(selectReportId);
    const isPublished = useAppSelector(selectIsReportPublished);
    const clusterId = useAppSelector(selectClusterId);
    const multiSelectLabel = useAppSelector(selectSelectedLabel);
    const chunkLinkingState = useChunkLinking();

    const { isActive, multiSelectChunks, confirmMultiSelectChunks, cancelMultiSelect } = useLabelChunkMultiSelect(props.targetDocumentId);

    const [docsetId, fileId] = useMemo(() => {
        if (!!props.targetDocumentId) {
            const fid = createFolderId(props.targetDocumentId);
            return [fid.clusterId, fid.fileId];
        }
        else {
            return [undefined, undefined];
        }
    }, [props.targetDocumentId]);

    useGetQualityControlWarningsQuery(!!docsetId && !!fileId ? { docsetId, fileId } : skipToken);

    return (
        <DocumentViewContext.Provider value={{ chunkLinkingState: chunkLinkingState }}>
            <DocumentView>
                <CanvasContainer>
                    <DocumentStateNotification />
                    <SingleDocumentViewContainer>
                        <FilmStrip documentId={props.targetDocumentId} />
                        <DocumentContainer>
                            <ReportDocumentNavButton
                                documentIds={props.documentIds}
                                currentDocumentId={props.targetDocumentId}
                                direction={NavDirection.Left} />
                            <WebDocHost
                                documentId={props.targetDocumentId}
                                clusterId={clusterId}
                                chunkLinkingProps={chunkLinkingState}
                                setPropertiesHidden={setPropertiesHidden}
                                propertiesHidden={propertiesHidden}
                                multiSelectProps={{
                                    isActive: isActive,
                                    multiSelectSpans: multiSelectChunks,
                                    selectedLabel: multiSelectLabel,
                                    onComplete: confirmMultiSelectChunks,
                                    onCancel: cancelMultiSelect
                                }} />
                            <ReportDocumentNavButton
                                documentIds={props.documentIds}
                                currentDocumentId={props.targetDocumentId}
                                direction={NavDirection.Right} />
                        </DocumentContainer>
                    </SingleDocumentViewContainer>
                </CanvasContainer>
                <TaskPanesContainer>
                    <ReportsWorkPane
                        readOnly={isPublished}
                        docSetId={clusterId}
                        documentId={props.targetDocumentId}
                        reportId={reportId} />
                </TaskPanesContainer>
                <ChunkDragLayer />
            </DocumentView>
        </DocumentViewContext.Provider>
    )
}
